import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';

interface UpcomingSlot {
  label: string;
  time_range: string;
}

interface DeliveryZone {
  title: string;
  anchor: string;
  minimum_label: string;
  free_label: string;
  cost_lines: string[];
  upcoming: UpcomingSlot[];
  unavailable: string[];
}

interface DeliveryShift {
  label: string;
  range: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface DeliveryPageProps {
  zones: DeliveryZone[];
  shifts: DeliveryShift[];
  orderingRules: string[];
  holidayNote: string;
  steps: string[];
  faqs: FaqItem[];
  intro: string;
  appUrl: string;
  title: string;
  description: string;
}

export default function DeliveryPage({
  zones,
  shifts,
  orderingRules,
  holidayNote,
  steps,
  faqs,
  intro,
  appUrl,
  title,
  description,
}: DeliveryPageProps) {
  const canonical = `${appUrl}/delivery`;
  const datedZones = zones.filter((zone) => zone.upcoming.length > 0 || zone.unavailable.length > 0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <MainLayout>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Head>

      <div className="bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-6xl px-4">
          <header className="mb-10 max-w-3xl">
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Delivery de verdulería en Córdoba
            </h1>
            <p className="text-lg leading-relaxed text-gray-600">{intro}</p>
          </header>

          <section className="mb-14" aria-labelledby="zonas-costos">
            <h2 id="zonas-costos" className="mb-2 text-2xl font-bold text-gray-900">
              Zonas, costos de envío y pedido mínimo
            </h2>
            <p className="mb-5 max-w-3xl text-gray-600">
              El nombre de tu barrio puede no figurar en la tabla: la zona sale del mapa de reparto.
              Comprobá tu dirección para saber si llegamos.
            </p>

            {zones.length === 0 ? (
              <p className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
                No hay zonas de entrega cargadas.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-left text-sm">
                  <caption className="sr-only">
                    Zonas de entrega con pedido mínimo, costo de envío y monto para envío gratis
                  </caption>
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Zona</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Pedido mínimo</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Costo de envío</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Envío gratis desde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((zone) => (
                      <tr key={zone.anchor} id={zone.anchor} className="border-t border-gray-100 align-top">
                        <th scope="row" className="px-4 py-3 font-semibold text-gray-900">{zone.title}</th>
                        <td className="px-4 py-3 text-gray-700">{zone.minimum_label}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {zone.cost_lines.map((line, index) => (
                            <div key={`${zone.anchor}-${index}`}>{line}</div>
                          ))}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{zone.free_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-3 text-sm text-gray-500">
              Los montos están en pesos. Cuando hay más de un costo, cada uno aplica desde el monto indicado.
            </p>
            <p className="mt-2 text-sm">
              <a href="/#zonas" className="font-semibold text-green-700 underline hover:text-green-800">
                Comprobá tu dirección
              </a>
            </p>
            <p className="mt-2 text-sm text-gray-700">
              Si estás mirando el bolsón, el total con el envío de cada zona está en{' '}
              <a href="/bolson-de-verduras" className="font-semibold text-green-700 underline hover:text-green-800">
                Bolsón de verduras a domicilio
              </a>
              .
            </p>
            <p className="mt-2 text-sm text-gray-700">
              El reparto lo hace nuestro equipo, en todas las zonas.{' '}
              <Link href="/por-que-elegirnos" className="font-semibold text-green-700 underline hover:text-green-800">
                por qué repartimos con equipo propio
              </Link>
            </p>
          </section>

          <section className="mb-14" aria-labelledby="horarios">
            <h2 id="horarios" className="mb-4 text-2xl font-bold text-gray-900">
              Días, horarios y horario de corte
            </h2>

            {shifts.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Turnos</h3>
                <ul className="space-y-1 text-gray-700">
                  {shifts.map((shift) => (
                    <li key={shift.range}>
                      {shift.label ? `${shift.label}: ` : ''}
                      {shift.range}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {orderingRules.length > 0 && (
              <div className="mb-6 max-w-3xl">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Hasta qué hora podés pedir</h3>
                <ul className="list-disc space-y-2 pl-5 text-gray-700">
                  {orderingRules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
                {holidayNote && (
                  <p className="mt-3 text-gray-700">{holidayNote}</p>
                )}
              </div>
            )}

            <h3 className="mb-3 text-lg font-semibold text-gray-900">Próximas entregas</h3>
            {datedZones.length === 0 ? (
              <p className="text-gray-600">No hay fechas de entrega próximas cargadas.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {datedZones.map((zone) => (
                  <article key={zone.anchor} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h4 className="mb-2 font-semibold text-gray-900">
                      <a href={`#${zone.anchor}`} className="hover:text-green-700">{zone.title}</a>
                    </h4>
                    {zone.upcoming.length > 0 && (
                      <ul className="space-y-1 text-sm text-gray-700">
                        {zone.upcoming.map((slot) => (
                          <li key={`${slot.label}-${slot.time_range}`}>
                            <span className="capitalize">{slot.label}</span>
                            <span className="text-gray-400"> · </span>
                            <span className="font-medium text-green-700">{slot.time_range}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {zone.unavailable.length > 0 && (
                      <p className="mt-3 text-sm text-gray-600">
                        En esta zona no se entrega: {zone.unavailable.join('; ')}.
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="mb-14" aria-labelledby="como-pedir">
            <h2 id="como-pedir" className="mb-4 text-2xl font-bold text-gray-900">
              Cómo hacer un pedido
            </h2>
            <ol className="mb-6 max-w-3xl list-decimal space-y-2 pl-5 text-gray-700">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <div className="flex flex-wrap gap-3">
              <Link
                href="/productos"
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                Ver productos
              </Link>
              <Link
                href="/packs"
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Ver packs
              </Link>
              <Link
                href="/carrito"
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Ir al carrito
              </Link>
            </div>
          </section>

          <section aria-labelledby="preguntas">
            <h2 id="preguntas" className="mb-6 text-2xl font-bold text-gray-900">
              Preguntas frecuentes
            </h2>
            <div className="max-w-3xl space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <p className="mt-2 leading-relaxed text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
